// ============================================================
// useFileUrl (v8.363.9) - a URL for a stored file, re-resolved when it dies.
//
// PocketBase's protected-file URLs carry a token that expires; Firebase's
// download URLs do not, but the port promises nothing either way. The hook
// asks once per (doc, field, ref), and again when the image fails to load
// (`retry`) or the tab comes back into view after a while.
// ============================================================
import { useCallback, useEffect, useRef, useState } from 'react';
import type { MutableRefObject } from 'react';

type Resolve = (ref: string) => Promise<string>;

const STALE_MS = 60_000;

interface Resolved {
  url: string | null;
  error: string | null;
}

const errorText = (err: unknown) => (err instanceof Error ? err.message : String(err));

export default function useFileUrl(ref: string, resolve: Resolve): Resolved & { retry: () => void } {
  const [tick, setTick] = useState(0);
  const askedAt = useRef(0);
  // The resolver is read through a ref: callers pass an inline closure and a
  // dependency on it would resolve, set state, re-render and resolve again.
  const resolver = useRef(resolve);
  resolver.current = resolve;
  const state = useResolved(ref, tick, resolver, askedAt);
  const retry = useCallback(() => setTick((t) => t + 1), []);
  useAskAgainOnReturn(ref, askedAt, retry);
  return { url: state.url, error: state.error, retry };
}

/** Resolve `ref` (again on every `tick`); a reply for a ref since replaced is dropped. */
function useResolved(
  ref: string,
  tick: number,
  resolver: MutableRefObject<Resolve>,
  askedAt: MutableRefObject<number>,
): Resolved {
  const [state, setState] = useState<Resolved>({ url: null, error: null });
  useEffect(() => {
    if (!ref) {
      setState({ url: null, error: null });
      return;
    }
    let live = true;
    askedAt.current = Date.now();
    resolver.current(ref).then(
      (url) => live && setState({ url, error: null }),
      (err: unknown) => live && setState({ url: null, error: errorText(err) }),
    );
    return () => {
      live = false;
    };
  }, [ref, tick, resolver, askedAt]);
  return state;
}

/** When the tab comes back into view more than a minute after the last ask, ask again. */
function useAskAgainOnReturn(ref: string, askedAt: MutableRefObject<number>, retry: () => void) {
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const stale = () => !!ref && Date.now() - askedAt.current > STALE_MS;
    const onVisible = () => {
      if (document.visibilityState === 'visible' && stale()) retry();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [ref, askedAt, retry]);
}
